import { createRouter, createWebHistory } from "vue-router";
import DashboardView from "@/views/dashboard/DashboardView.vue";
import StoreDashboardView from "@/views/dashboard/StoreDashboardView.vue";
import StorePromoCodesView from "@/views/dashboard/StorePromoCodesView.vue";
import StoreSharedDashboardView from "@/views/dashboard/StoreSharedDashboardView.vue";
import StoreCatalogView from "@/views/dashboard/StoreCatalogView.vue";
import StoreReportsView from "@/views/dashboard/StoreReportsView.vue";
import StoreReportDesignerView from "@/views/dashboard/StoreReportDesignerView.vue";
import StoreUsersView from "@/views/dashboard/StoreUsersView.vue";
import UsersView from "@/views/dashboard/UsersView.vue";
import StoresView from "@/views/dashboard/StoresView.vue";
import PhoneLoginView from "@/views/auth/PhoneLoginView.vue";
import EmailVerificationView from "@/views/EmailVerificationView.vue";
import EmailPreferencesView from "@/views/EmailPreferencesView.vue";
import store from "../store";
import StoreSettingsView from "@/views/dashboard/StoreSettingsView.vue";
import AdminJackView from "@/views/dashboard/AdminJackView.vue";
import StoreAdminView from "@/views/dashboard/StoreAdminView.vue";
import AdminSubscriptionsView from "@/views/dashboard/AdminSubscriptionsView.vue";
import AdminEmailVerificationView from "@/views/dashboard/AdminEmailVerificationView.vue";
import AdminReleasesView from "@/views/dashboard/AdminReleasesView.vue";
import AdminGroupsView from "@/views/dashboard/AdminGroupsView.vue";
import AdminDeployView from "@/views/dashboard/AdminDeployView.vue";
import QuickbooksIntegrationRedirectView from "@/views/dashboard/QuickbooksIntegrationRedirectView.vue";
import { normalizedFeatureValue } from "@/features/insights/model";
import { firstPermittedOrdersRoute } from "@/features/orders/model";

export function defaultStoreExperience(userInfo, stores) {
  if (!stores?.length) return "/store/no-stores";
  let cachedInsightsEligibility = null;
  try {
    const cached = sessionStorage.getItem("insights-eligible");
    cachedInsightsEligibility = cached === null ? null : cached === "true";
  } catch (_) {
    cachedInsightsEligibility = null;
  }
  const insightsDisabled =
    normalizedFeatureValue(userInfo, "INSIGHTS") === false ||
    cachedInsightsEligibility === false;
  return insightsDisabled
    ? `/store/${stores[0].id}/dashboard`
    : "/insights";
}

const routes = [
  {
    path: "/",
    component: () => import("@/layouts/AuthLayout.vue"),
    children: [
      {
        path: "",
        name: "login",
        component: PhoneLoginView,
      },
    ],
  },
  {
    path: "/auth/session",
    name: "auth_session",
    component: () => import("@/views/auth/AuthSessionView.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/verify-email",
    name: "verify_email",
    component: EmailVerificationView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/email-preferences",
    name: "email_preferences",
    component: EmailPreferencesView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/report-templates/:templateUuid",
    name: "public_report_template",
    component: () =>
      import("@/views/reports/PublicReportTemplateView.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/settings/integrations/quickbooks",
    name: "quickbooks_integration_redirect",
    component: QuickbooksIntegrationRedirectView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/dashboard",
    redirect: "/admin/dashboard",
  },
  {
    path: "/insights",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      {
        path: "",
        name: "insights",
        component: () => import("@/views/dashboard/InsightsView.vue"),
        meta: {
          requiresAuth: true,
          requiresFeature: "INSIGHTS",
          subtitle: "Insights",
        },
      },
    ],
  },
  {
    path: "/store",
    component: () => import("@/layouts/DashboardLayout.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/store/no-stores",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      {
        path: "",
        name: "no_stores",
        component: () => import("@/views/dashboard/NoStoresView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Create Store",
        },
      },
    ],
  },
  {
    path: "/admin",
    component: () => import("@/layouts/DashboardLayout.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },

    children: [
      {
        path: "dashboard",
        name: "dashboard",
        component: DashboardView,
        meta: {
          requiresAuth: true,
          subtitle: "Admin Dashboard",
        },
        default: true,
      },
      {
        path: "users",
        name: "users",
        component: UsersView,
      },
      {
        path: "stores",
        name: "admin_stores",
        component: StoresView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Stores",
        },
      },
      {
        path: "catalog",
        name: "catalog",
        component: StoreCatalogView,
        meta: {
          requiresAuth: true,
          subtitle: "Store Catalog",
        },
      },
      {
        path: "subscriptions",
        name: "admin_subscriptions",
        component: AdminSubscriptionsView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Subscriptions",
        },
      },
      {
        path: "email-verification",
        name: "admin_email_verification",
        component: AdminEmailVerificationView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Client Management",
        },
      },
      {
        path: "releases",
        name: "admin_releases",
        component: AdminReleasesView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "App Releases",
        },
      },
      {
        path: "groups",
        name: "admin_groups",
        component: AdminGroupsView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Groups",
        },
      },
      {
        path: "item-sync",
        name: "item-sync",
        component: () => import("@/views/dashboard/StoreItemSyncView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Store Item Sync",
        },
      },
      {
        path: "purchases",
        name: "admin_purchases",
        // Route alias that redirects to the store-scoped purchases using selectedStore
        redirect: () => {
          const existingStores = store.getters.getUserStores || [];
          const selectedStore = store.getters.getSelectedStore;
          const targetStoreId =
            selectedStore || (existingStores[0] && existingStores[0].id);
          return targetStoreId
            ? `/store/${targetStoreId}/purchases`
            : "/store/no-stores";
        },
        meta: {
          requiresAuth: true,
          subtitle: "Purchases",
        },
      },
      {
        path: "expenses",
        name: "admin_expenses",
        redirect: () => {
          const existingStores = store.getters.getUserStores || [];
          const selectedStore = store.getters.getSelectedStore;
          const targetStoreId =
            (typeof selectedStore === "object" && selectedStore !== null
              ? selectedStore.id
              : selectedStore) || (existingStores[0] && existingStores[0].id);
          return targetStoreId
            ? `/store/${targetStoreId}/expenses`
            : "/store/no-stores";
        },
        meta: {
          requiresAuth: true,
          subtitle: "Expenses",
        },
      },
      {
        path: "jack",
        name: "jack",
        component: AdminJackView,
        meta: {
          requiresAuth: true,
          subtitle: "Jack Access",
        },
      },
      {
        path: "deploy",
        name: "admin_deploy",
        component: AdminDeployView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Staging Deploy",
        },
      },
    ],
  },
  {
    path: "/store/:storeId",
    component: () => import("@/layouts/DashboardLayout.vue"),
    children: [
      {
        path: "dashboard",
        name: "store_dashboard",
        component: StoreDashboardView,
        meta: {
          requiresAuth: true,
          subtitle: "Briefs",
        },
        default: true,
      },
      {
        path: "promos",
        name: "promos",
        component: StorePromoCodesView,
        meta: {
          requiresAuth: true,
          subtitle: "Promo Codes",
        },
        default: true,
      },
      {
        path: "reports/designer",
        name: "report_designer",
        component: StoreReportDesignerView,
        meta: {
          requiresAuth: true,
          subtitle: "Report Designer",
        },
      },
      {
        path: "reports",
        name: "reports",
        component: StoreReportsView,
        meta: {
          requiresAuth: true,
          subtitle: "Reports & Backups",
        },
      },
      {
        path: "catalog",
        name: "catalog",
        component: StoreCatalogView,
        meta: {
          requiresAuth: true,
          subtitle: "Catalog",
        },
        default: true,
      },
      {
        path: "settings",
        name: "settings",
        component: StoreSettingsView,
        meta: {
          requiresAuth: true,
          subtitle: "Settings",
        },
        default: true,
      },
      {
        path: "users",
        name: "store_users",
        component: StoreUsersView,
        meta: {
          requiresAuth: true,
          subtitle: "Users",
        },
      },
      {
        path: "settings/integrations/quickbooks",
        name: "store_settings_quickbooks",
        component: StoreSettingsView,
        meta: {
          requiresAuth: true,
          subtitle: "Settings",
        },
      },
      {
        path: "admin",
        name: "store_admin",
        component: StoreAdminView,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          subtitle: "Admin",
        },
      },
      {
        path: "orders",
        name: "orders",
        component: () => import("@/views/dashboard/OrdersView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Orders",
        },
        children: [
          {
            path: "",
            name: "orders_default",
            redirect: (to) => {
              const resolver = store.getters.getStoreById;
              const currentStore =
                typeof resolver === "function"
                  ? resolver(to.params.storeId)
                  : (store.getters.getUserStores || []).find(
                      (entry) => String(entry.id) === String(to.params.storeId),
                    );
              return {
                name: firstPermittedOrdersRoute(currentStore),
                params: { storeId: to.params.storeId },
              };
            },
          },
          {
            path: "transactions",
            name: "orders_transactions",
            component: () =>
              import("@/views/dashboard/purchases/TransactionsView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Transactions",
            },
          },
          {
            path: "tickets",
            name: "orders_tickets",
            component: () =>
              import("@/views/dashboard/purchases/TicketsView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Tickets",
            },
          },
          {
            path: "incoming-orders",
            name: "orders_incoming_orders",
            component: () =>
              import("@/views/dashboard/purchases/IncomingOrdersView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Incoming Orders",
            },
          },
          {
            path: "fulfillment-tickets",
            name: "orders_fulfillment_tickets",
            component: () =>
              import("@/views/dashboard/purchases/FulfillmentTicketsView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Fulfillment Tickets",
            },
          },
          {
            path: "product-mappings",
            name: "orders_product_mappings",
            component: () =>
              import("@/views/dashboard/purchases/ProductMappingsView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Product Linking",
            },
          },
        ],
      },
      {
        path: "purchases",
        name: "purchases",
        component: () => import("@/views/dashboard/PurchasesView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Purchases",
        },
        children: [
          {
            path: "",
            name: "purchases_default",
            redirect: { name: "purchases_purchase_orders" },
          },
          {
            path: "purchase-orders",
            name: "purchases_purchase_orders",
            component: () =>
              import("@/views/dashboard/purchases/OrdersView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Purchase Orders",
            },
          },
          {
            path: "purchase-orders/:orderId",
            name: "purchases_purchase_order_detail",
            component: () =>
              import("@/views/dashboard/purchases/OrderDetailView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Purchase Order Detail",
            },
          },
          {
            path: "receipts",
            name: "purchases_receipts",
            component: () =>
              import("@/views/dashboard/purchases/ReceiptsView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Goods Receipts",
            },
          },
          {
            path: "bulk-purchase",
            name: "purchases_bulk_purchase",
            component: () =>
              import("@/views/dashboard/purchases/BulkPurchaseView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Bulk Purchase",
            },
          },
          {
            path: "suppliers",
            name: "purchases_suppliers",
            component: () =>
              import("@/views/dashboard/purchases/SuppliersView.vue"),
            meta: {
              requiresAuth: true,
              subtitle: "Suppliers",
            },
          },
        ],
      },
      {
        path: "assets",
        name: "store_assets",
        component: () =>
          import("@/views/dashboard/assets/StoreAssetsView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Assets",
        },
      },
      {
        path: "expenses",
        name: "store_expenses",
        component: () => import("@/views/dashboard/StoreExpensesView.vue"),
        meta: {
          requiresAuth: true,
          subtitle: "Expenses",
        },
      },
    ],
  },
  {
    path: "/store/dashboard/shared/:token",
    name: "store_shared_dashboard",
    component: StoreSharedDashboardView,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  let userInfo = store.getters.getAuthenticatedUser;
  const requiresAuth = to.matched.some(
    (record) => record.meta && record.meta.requiresAuth,
  );
  const requiresAdmin = to.matched.some(
    (record) => record.meta && record.meta.requiresAdmin,
  );
  const requiredFeature = to.matched
    .map((record) => record.meta?.requiresFeature)
    .find(Boolean);

  // If navigating to login ("/")
  if (to.path === "/") {
    if (userInfo) {
      // Check if stores are already available in state first
      const existingStores = store.getters.getUserStores || [];
      if (existingStores.length > 0) {
        return next(defaultStoreExperience(userInfo, existingStores));
      }

      // Try to fetch stores from API
      try {
        const storesResponse = await store.dispatch("getAllUsersStore");
        const stores = storesResponse?.stores || [];
        if (stores && stores.length > 0) {
          return next(defaultStoreExperience(userInfo, stores));
        }
        // No stores available - redirect to no-stores page
        return next("/store/no-stores");
      } catch (error) {
        console.error("Failed to fetch stores:", error);
        // Check localStorage as fallback
        try {
          const localStores = JSON.parse(
            localStorage.getItem("stores") || "[]",
          );
          if (localStores && localStores.length > 0) {
            return next(defaultStoreExperience(userInfo, localStores));
          }
        } catch (localError) {
          console.error("Failed to parse localStorage stores:", localError);
        }
        // Only redirect to no-stores if we're certain there are no stores
        return next("/store/no-stores");
      }
    }
    // Do not attempt to recover session here to avoid loops; stay on login
    return next();
  }

  // Handle /store redirect
  if (to.path === "/store") {
    if (userInfo) {
      // Check if stores are already available in state first
      const existingStores = store.getters.getUserStores || [];
      if (existingStores.length > 0) {
        return next(defaultStoreExperience(userInfo, existingStores));
      }

      // Try to fetch stores from API
      try {
        const storesResponse = await store.dispatch("getAllUsersStore");
        const stores = storesResponse?.stores || [];
        if (stores && stores.length > 0) {
          return next(defaultStoreExperience(userInfo, stores));
        }
        // No stores available - redirect to no-stores page
        return next("/store/no-stores");
      } catch (error) {
        console.error("Failed to fetch stores:", error);
        // Check localStorage as fallback
        try {
          const localStores = JSON.parse(
            localStorage.getItem("stores") || "[]",
          );
          if (localStores && localStores.length > 0) {
            return next(defaultStoreExperience(userInfo, localStores));
          }
        } catch (localError) {
          console.error("Failed to parse localStorage stores:", localError);
        }
        // Only redirect to no-stores if we're certain there are no stores
        return next("/store/no-stores");
      }
    }
    return next("/");
  }

  // Routes requiring auth
  if (requiresAuth) {
    // Show loading only if we don't have user in store yet
    let loadingContainer = document.querySelector(".loading-container");
    if (!userInfo && !loadingContainer) {
      const myDiv = document.body;
      const loadingEl = document.createElement("div");
      loadingEl.setAttribute("class", "loading-container");
      loadingEl.innerHTML = `
        <div class="h-screen z-20 w-full fixed top-0 bg-gray-300 dark:bg-slate-600 flex items-center justify-center flex-col">
          <div class="loading-content">
            <h2>HovaStore</h2>
            <h2>HovaStore</h2>
          </div>
        </div>
      `;
      myDiv.appendChild(loadingEl);
      loadingContainer = loadingEl;
    }

    try {
      if (!userInfo) {
        await store.dispatch("getUserInfo");
        userInfo = store.getters.getAuthenticatedUser;
      }
    } catch (error) {
      next("/");
      return;
    } finally {
      const lc = document.querySelector(".loading-container");
      if (lc) lc.remove();
    }

    if (requiresAdmin) {
      const hasAdminPermissions = store.getters.hasAdminPermissions;
      if (!hasAdminPermissions) {
        return next("/");
      }
    }

    if (requiredFeature && normalizedFeatureValue(userInfo, requiredFeature) === false) {
      return next("/store");
    }

    return next();
  }

  // Public routes
  return next();
});

export default router;
